<template>
  <BaseBadge
    :align="align"
    :color="color"
    size="sm"
    test-id="planStatusBadge"
    :variant="variant"
  >
    <VisuallyHidden>Plan status: </VisuallyHidden>
    {{ formatLabel }}
  </BaseBadge>
</template>

<script setup lang="ts">
import { type PaymentPlanStatusEnum } from '@/types';

type BadgeColor = 'yellow' | 'gray' | 'blue' | 'red' | 'amber' | 'green' | 'teal' | 'purple' | 'pink';

interface Props {
  /**
   * Plan status value
   * Can be a string or typed PlanStatus
   */
  status: string | PaymentPlanStatusEnum;

  /**
   * Badge variant style
   * @default 'solid'
   */
  variant?: 'solid' | 'outline';

  /**
   * Badge alignment
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'solid',
  align: 'left',
});

const formatLabel = computed(() => {
  if (!props.status) return 'Unknown';
  const statusStr = String(props.status);
  return (statusStr.charAt(0).toUpperCase() + statusStr.slice(1)).replace(/-/g, ' ');
});

/**
 * Map plan statuses to design system colors
 */
const color = computed((): BadgeColor => {
  if (!props.status) return 'gray';

  const statusLower = String(props.status).toLowerCase();

  const statusMap = {
    active: 'blue',
    'awaiting ticketing': 'blue',
    ontrack: 'blue',
    paused: 'amber',
    halted: 'amber',
    cancelled: 'red',
    terminated: 'red',
    suspended: 'purple',
    completed: 'green',
    finished: 'green',
    late: 'amber',
    late0d: 'amber',
    late7d: 'amber',
    late14d: 'amber',
    lossrecovery: 'amber',
    stopped: 'red',
    upcoming: 'blue',
    failed: 'red',
    retrying: 'amber',
    refunded: 'purple',
    'partially-refunded': 'yellow',
  } as const;

  return statusMap[statusLower as keyof typeof statusMap] ?? 'gray';
});
</script>
